/**
 * SDK do Mercado Pago (frontend) — Checkout Transparente para a loja de eBooks.
 * Tokeniza o cartão NO NAVEGADOR (o número do cartão nunca vai ao backend) e
 * descobre a bandeira/parcelas. A Public Key é pública por design.
 */

const MP_PUBLIC_KEY =
  (typeof window !== 'undefined' && window.__REACT_APP_MP_PUBLIC_KEY) ||
  process.env.REACT_APP_MP_PUBLIC_KEY ||
  'APP_USR-3042cc83-3248-4144-acbe-3f6d0274450d';

const SDK_URL = 'https://sdk.mercadopago.com/js/v2';

let sdkPromise = null;
function loadSdk() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.MercadoPago) return Promise.resolve();
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SDK_URL;
    s.async = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error('Falha ao carregar o SDK do Mercado Pago'));
    document.head.appendChild(s);
  });
  return sdkPromise;
}

let mpInstance = null;
export async function getMp() {
  await loadSdk();
  if (!mpInstance) {
    mpInstance = new window.MercadoPago(MP_PUBLIC_KEY, { locale: 'pt-BR' });
  }
  return mpInstance;
}

// ===== Device ID (fingerprint antifraude do Mercado Pago) =====
// O security.js do MP cria window.MP_DEVICE_SESSION_ID ao carregar. Enviamos esse
// valor ao backend, que o repassa no header X-Meli-Session-Id ao criar o
// pagamento — AUMENTA a aprovação do cartão (reduz cc_rejected_high_risk).
let securityPromise = null;
function loadSecurityScript() {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.MP_DEVICE_SESSION_ID) return Promise.resolve();
  if (securityPromise) return securityPromise;
  securityPromise = new Promise((resolve) => {
    const s = document.createElement('script');
    s.src = 'https://www.mercadopago.com/v2/security.js';
    s.setAttribute('view', 'checkout');
    s.async = true;
    // Não bloquear o checkout se o script falhar — device_id é um "extra".
    s.onload = () => resolve();
    s.onerror = () => resolve();
    document.head.appendChild(s);
  });
  return securityPromise;
}

// Dispara o carregamento do security.js já na importação do módulo.
loadSecurityScript();

/** Retorna o Device ID do MP (ou undefined se ainda não disponível). */
export async function getDeviceId() {
  try {
    await loadSecurityScript();
    for (let i = 0; i < 20 && !window.MP_DEVICE_SESSION_ID; i++) {
      await new Promise((r) => setTimeout(r, 100));
    }
    return window.MP_DEVICE_SESSION_ID || undefined;
  } catch {
    return undefined;
  }
}

// Mensagens amigáveis para os erros do SDK (por código OU descrição).
const CARD_TOKEN_ERRORS = {
  205: 'Digite o número do cartão.',
  208: 'Selecione o mês de validade.',
  209: 'Selecione o ano de validade.',
  212: 'Informe o CPF do titular.',
  213: 'Informe o CPF do titular.',
  214: 'Informe o CPF do titular.',
  220: 'Informe o CPF do titular.',
  221: 'Digite o nome impresso no cartão.',
  224: 'Digite o código de segurança (CVV).',
  E301: 'Número do cartão inválido. Confira os dígitos.',
  E302: 'Código de segurança (CVV) inválido.',
  316: 'Nome do titular inválido.',
  322: 'CPF inválido.',
  323: 'CPF inválido.',
  324: 'CPF inválido.',
  325: 'Mês de validade inválido.',
  326: 'Ano de validade inválido.',
};

function matchCardDescription(desc) {
  const d = String(desc || '').toLowerCase();
  if (!d) return null;
  if (d.includes('card_number') || d.includes('cardnumber')) return 'Número do cartão inválido. Confira os dígitos.';
  if (d.includes('security_code') || d.includes('securitycode') || d.includes('cvv')) return 'Código de segurança (CVV) inválido.';
  if (d.includes('expiration') || d.includes('card_expiration')) return 'Data de validade inválida.';
  if (d.includes('cardholder') || d.includes('holder')) return 'Nome do titular inválido.';
  if (d.includes('identification') || d.includes('doc_number') || d.includes('docnumber') || d.includes('cpf')) return 'CPF inválido.';
  if (d.includes('installment')) return 'Esse cartão não permite esse número de parcelas.';
  return null;
}

export function friendlyCardError(err) {
  const causes = err?.cause || err?.errors || (Array.isArray(err) ? err : null);
  if (Array.isArray(causes) && causes.length) {
    for (const c of causes) {
      const code = c?.code ?? c?.error_code;
      if (code != null && CARD_TOKEN_ERRORS[code]) return CARD_TOKEN_ERRORS[code];
      const byDesc = matchCardDescription(c?.description || c?.message);
      if (byDesc) return byDesc;
    }
  }
  return matchCardDescription(err?.message || err?.description || err);
}

/** Descobre o payment_method_id a partir do BIN (6-8 primeiros dígitos). */
export async function getPaymentMethodId(cardNumber) {
  const bin = String(cardNumber).replace(/\D/g, '').slice(0, 8);
  if (bin.length < 6) throw new Error('Número do cartão inválido. Confira os dígitos.');
  const mp = await getMp();
  let resp;
  try {
    resp = await mp.getPaymentMethods({ bin });
  } catch (err) {
    throw new Error(friendlyCardError(err) || 'Não foi possível identificar a bandeira do cartão.');
  }
  const method = resp?.results?.[0];
  if (!method?.id) throw new Error('Não foi possível identificar a bandeira do cartão.');
  return method.id;
}

/** Consulta as parcelas VÁLIDAS + payment_method_id + issuer_id para o cartão. */
export async function getInstallmentsInfo({ cardNumber, amount }) {
  const bin = String(cardNumber).replace(/\D/g, '').slice(0, 8);
  if (bin.length < 6) throw new Error('Número do cartão inválido. Confira os dígitos.');
  const mp = await getMp();
  let resp;
  try {
    resp = await mp.getInstallments({ amount: String(amount), bin, paymentTypeId: 'credit_card' });
  } catch (err) {
    throw new Error(friendlyCardError(err) || 'Não foi possível validar o cartão. Confira os dados.');
  }
  const info = Array.isArray(resp) ? resp[0] : resp;
  const payerCosts = Array.isArray(info?.payer_costs) ? info.payer_costs : [];
  return {
    paymentMethodId: info?.payment_method_id,
    issuerId: info?.issuer?.id != null ? String(info.issuer.id) : undefined,
    allowed: payerCosts.map((pc) => pc.installments),
  };
}

// ===== Secure Fields (PCI Compliance) =====
// Número, validade e CVV são digitados em IFRAMES do Mercado Pago — esses dados
// NUNCA passam pelo DOM/JS da loja. Atende ao requisito "Formulário de Cartões -
// PCI Compliance" (SAQ-A).
let secureFields = null;
// Tema ESCURO da loja: texto branco, placeholder dourado (combina com os inputs).
const SECURE_FIELD_STYLE = {
  'font-size': '15px',
  color: '#ffffff',
  placeholderColor: '#C9A227',
};

/** Monta os 3 Secure Fields nos containers (ids fixos). Idempotente. */
export async function mountSecureFields() {
  const mp = await getMp();
  if (secureFields) return secureFields;
  const cardNumber = mp.fields
    .create('cardNumber', { placeholder: '0000 0000 0000 0000', style: SECURE_FIELD_STYLE })
    .mount('mp-ebooks-card-number');
  const expirationDate = mp.fields
    .create('expirationDate', { placeholder: 'MM/AA', style: SECURE_FIELD_STYLE })
    .mount('mp-ebooks-card-expiry');
  const securityCode = mp.fields
    .create('securityCode', { placeholder: 'CVV', style: SECURE_FIELD_STYLE })
    .mount('mp-ebooks-card-cvv');
  secureFields = { cardNumber, expirationDate, securityCode };
  return secureFields;
}

/** Desmonta os Secure Fields (ao sair do cartão / desmontar o componente). */
export function unmountSecureFields() {
  if (!secureFields) return;
  for (const f of Object.values(secureFields)) {
    try { f.unmount(); } catch (e) { /* ignore */ }
  }
  secureFields = null;
}

/**
 * Tokeniza a partir dos Secure Fields (número NUNCA toca nosso JS). Só o nome do
 * titular e o CPF (não sensíveis) vêm do nosso formulário. Retorna o id do token
 * + os 6 primeiros dígitos (BIN) para parcelas/bandeira.
 */
export async function createCardTokenFromFields({ cardholderName, identificationNumber }) {
  const mp = await getMp();
  try {
    const token = await mp.fields.createCardToken({
      cardholderName,
      identificationType: 'CPF',
      identificationNumber: String(identificationNumber).replace(/\D/g, ''),
    });
    if (!token?.id) throw new Error('Confira os dados do cartão e tente novamente.');
    return { id: token.id, firstSixDigits: token.first_six_digits || '' };
  } catch (err) {
    throw new Error(friendlyCardError(err) || 'Confira os dados do cartão e tente novamente.');
  }
}

/** Bandeira (payment_method_id) a partir do BIN (6-8 primeiros dígitos). */
export async function getPaymentMethodByBin(bin) {
  const b = String(bin).replace(/\D/g, '').slice(0, 8);
  if (b.length < 6) throw new Error('Não foi possível identificar a bandeira do cartão.');
  const mp = await getMp();
  let resp;
  try {
    resp = await mp.getPaymentMethods({ bin: b });
  } catch (err) {
    throw new Error(friendlyCardError(err) || 'Não foi possível identificar a bandeira do cartão.');
  }
  const method = resp?.results?.[0];
  if (!method?.id) throw new Error('Não foi possível identificar a bandeira do cartão.');
  return method.id;
}

/** Parcelas válidas + emissor a partir do BIN + valor. */
export async function getInstallmentsByBin({ bin, amount }) {
  const b = String(bin).replace(/\D/g, '').slice(0, 8);
  if (b.length < 6) throw new Error('Número do cartão inválido.');
  const mp = await getMp();
  let resp;
  try {
    resp = await mp.getInstallments({ amount: String(amount), bin: b, paymentTypeId: 'credit_card' });
  } catch (err) {
    throw new Error(friendlyCardError(err) || 'Não foi possível validar o cartão.');
  }
  const info = Array.isArray(resp) ? resp[0] : resp;
  const payerCosts = Array.isArray(info?.payer_costs) ? info.payer_costs : [];
  return {
    paymentMethodId: info?.payment_method_id,
    issuerId: info?.issuer?.id != null ? String(info.issuer.id) : undefined,
    allowed: payerCosts.map((pc) => pc.installments),
  };
}

/** Tokeniza o cartão e retorna o token id. */
export async function createCardToken(card) {
  const mp = await getMp();
  try {
    const token = await mp.createCardToken({
      cardNumber: String(card.cardNumber).replace(/\D/g, ''),
      cardholderName: card.cardholderName,
      cardExpirationMonth: card.cardExpirationMonth,
      cardExpirationYear: card.cardExpirationYear,
      securityCode: card.securityCode,
      identificationType: 'CPF',
      identificationNumber: String(card.identificationNumber).replace(/\D/g, ''),
    });
    if (!token?.id) throw new Error('Confira os dados do cartão e tente novamente.');
    return token.id;
  } catch (err) {
    throw new Error(friendlyCardError(err) || 'Confira os dados do cartão e tente novamente.');
  }
}
